import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, Share2, Volume2, Loader2 } from "lucide-react";

export function PRCelebration({ pr, onClose }) {
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!pr) return;
    setMsg(""); setLoading(true);
    const end = Date.now() + 1400;
    const colors = ["#CCFF00", "#7C5CFF", "#ffffff"];
    (function frame() {
      confetti({ particleCount: 5, angle: 60, spread: 75, startVelocity: 55, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 5, angle: 120, spread: 75, startVelocity: 55, origin: { x: 1, y: 0.7 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    let alive = true;
    api.post("/api/logs/celebrate", { movement: pr.movement, detail: pr.detail || "" })
      .then(({ data }) => {
        if (!alive) return;
        setMsg(data.message); setLoading(false);
        if (data.audio_b64) {
          const a = new Audio(`data:audio/mpeg;base64,${data.audio_b64}`);
          audioRef.current = a; a.play().catch(() => {});
        }
      })
      .catch(() => { if (alive) { setMsg(`New PR on ${pr.movement} — that's the OCTON standard!`); setLoading(false); } });
    return () => { alive = false; audioRef.current?.pause(); };
  }, [pr]);

  const replay = () => audioRef.current?.play().catch(() => {});
  const share = async () => {
    const text = `${msg || `New PR on ${pr.movement}!`} 💪 Trained with OCTON Gym · octon-gym.com`;
    try {
      if (navigator.share) await navigator.share({ title: "OCTON GYM — New PR!", text });
      else { await navigator.clipboard.writeText(text); toast.success("PR shout-out copied — paste it anywhere!"); }
    } catch { /* user cancelled */ }
  };

  return (
    <Dialog open={!!pr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface border-acid/40 max-w-md text-center overflow-hidden" data-testid="pr-celebration">
        <DialogTitle className="sr-only">New personal record: {pr?.movement}</DialogTitle>
        <DialogDescription className="sr-only">OCTON celebrates your new personal record with a shareable shout-out.</DialogDescription>
        <div className="absolute inset-0 bg-gradient-to-b from-acid/10 to-transparent pointer-events-none" />
        <div className="relative pt-2">
          <div className="w-16 h-16 rounded-full bg-acid/15 border border-acid/50 flex items-center justify-center mx-auto animate-pulse">
            <Trophy className="w-8 h-8 text-acid" />
          </div>
          <p className="mono-label text-acid mt-4">new personal record</p>
          <h2 className="font-heading font-black text-3xl tracking-tight mt-1">{pr?.movement}</h2>
          {pr?.detail && <p className="text-[#8A91A6] mt-1">{pr.detail}</p>}
          <div className="mt-4 min-h-[3.5rem] flex items-center justify-center">
            {loading ? (
              <span className="mono-label inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> OCTON is hyping you up…</span>
            ) : (
              <p className="text-white/90 italic px-2">"{msg}"</p>
            )}
          </div>
          <div className="flex gap-2 justify-center mt-4">
            <Button onClick={replay} variant="outline" className="border-[#1E222A] rounded-full" data-testid="pr-replay-btn"><Volume2 className="w-4 h-4" /> Replay</Button>
            <Button onClick={share} className="bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="pr-share-btn"><Share2 className="w-4 h-4" /> Share</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
